import { SuggestedPersona } from "@/types/state";
import { supabase } from "@/integrations/supabase/client";

interface WizardProgress {
  timestamp: number;
  currentStep: string;
  selectedPath: "prebuilt" | "custom" | "braindump" | null;
  problem: string;
  context: string;
  brainDump: string;
  suggestedPersonas: SuggestedPersona[];
  prebuiltBoardId?: string;
  boardName?: string;
}

const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

let saveTimeout: NodeJS.Timeout | null = null;

export const saveWizardProgress = (
  userId: string,
  progress: Partial<WizardProgress>, 
  debounce = false,
  onSaved?: () => void
) => {
  const performSave = async () => {
    try {
      const timestamp = Date.now();
      const dataToSave = {
        ...progress,
        timestamp
      };

      const { error } = await supabase
        .from('board_drafts')
        .upsert({
          user_id: userId,
          step: progress.currentStep,
          path: progress.selectedPath,
          data: dataToSave,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });

      if (error) throw error;

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      console.error("Failed to save wizard progress:", error);
    }
  };

  if (debounce) {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    saveTimeout = setTimeout(performSave, 1000);
  } else {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    performSave();
  }
};

export const loadWizardProgress = async (userId: string): Promise<WizardProgress> => {
  try {
    const { data, error } = await supabase
      .from('board_drafts')
      .select('data')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    
    if (!data) {
      return getDefaultProgress();
    }

    const progress = data.data as WizardProgress;
    
    if (Date.now() - progress.timestamp > EXPIRY_MS) {
      await clearWizardProgress(userId);
      return getDefaultProgress();
    }

    return progress;
  } catch (error) {
    console.error("Failed to load wizard progress:", error);
    return getDefaultProgress();
  }
};

export const clearWizardProgress = async (userId: string) => {
  try {
    await supabase
      .from('board_drafts')
      .delete()
      .eq('user_id', userId);
      
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
  } catch (error) {
    console.error("Failed to clear wizard progress:", error);
  }
};

export const hasWizardProgress = async (userId: string): Promise<boolean> => {
  try {
    const progress = await loadWizardProgress(userId);
    return progress.timestamp > 0 && 
           (progress.currentStep !== "path-selection" || 
            progress.selectedPath !== null);
  } catch {
    return false;
  }
};

const getDefaultProgress = (): WizardProgress => ({
  timestamp: 0,
  currentStep: "path-selection",
  selectedPath: null,
  problem: "",
  context: "",
  brainDump: "",
  suggestedPersonas: []
});
